import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const MasterNumberCalculator = () => {
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (field, value) => {
    if (field === 'name') {
      setName(value);
    } else if (field === 'birthDate') {
      setBirthDate(value);
    }
    setError('');
  };

  const calculateMasterNumber = () => {
    if (!name || !birthDate) {
      setError('Please enter both your name and birth date');
      return;
    }

    const calculateLifePathNumber = (birthDate) => {
      const birthDateNumbers = birthDate.split('-').join('');
      let lifePath = birthDateNumbers.split('').reduce((acc, num) => acc + parseInt(num), 0);
      while (lifePath > 9 && lifePath !== 11 && lifePath !== 22 && lifePath !== 33) {
        lifePath = lifePath.toString().split('').reduce((acc, num) => acc + parseInt(num), 0);
      }
      return lifePath;
    };

    const calculateMasterNumber = (name, birthDate) => {
      const nameNumbers = name.toUpperCase().split('').reduce((acc, letter) => {
        const letterValue = letter.charCodeAt(0) - 64;
        return acc + (letterValue > 0 && letterValue <= 26 ? letterValue : 0);
      }, 0);
      
      const total = nameNumbers + parseInt(birthDate.split('-').join(''));
      let masterNumber = total.toString().split('').reduce((acc, num) => acc + parseInt(num), 0);
      
      while (masterNumber > 9 && masterNumber !== 11 && masterNumber !== 22 && masterNumber !== 33) {
        masterNumber = masterNumber.toString().split('').reduce((acc, num) => acc + parseInt(num), 0);
      }
      
      return masterNumber;
    };

    const lifePathNumber = calculateLifePathNumber(birthDate);
    const masterNumber = calculateMasterNumber(name, birthDate);

    const getMasterNumberMeaning = (number) => {
      const meanings = {
        11: 'Intuition and Spiritual Insight',
        22: 'Master Builder and Manifestation',
        33: 'Master Teacher and Compassion'
      };
      return meanings[number] || 'Unknown Meaning';
    };

    const masterFactors = {
      intuition: masterNumber === 11 ? 90 : 70,
      manifestation: masterNumber === 22 ? 85 : 65,
      compassion: masterNumber === 33 ? 80 : 60
    };

    const overallMasterScore = Object.values(masterFactors).reduce((acc, score) => acc + score, 0) / Object.values(masterFactors).length;

    const masterChartData = {
      labels: ['Intuition', 'Manifestation', 'Compassion'],
      datasets: [
        {
          label: 'Master Factors',
          data: Object.values(masterFactors),
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        }
      ]
    };

    setResults({
      name,
      birthDate,
      lifePathNumber,
      masterNumber,
      masterNumberMeaning: getMasterNumberMeaning(masterNumber),
      masterFactors,
      overallMasterScore: Math.round(overallMasterScore)
    });

    setChartData(masterChartData);
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Master Number Calculator | Calculate Your Master Number</title>
        <meta name="description" content="Calculate your master number using numerology. Understand your potential for intuition, manifestation, and compassion." />
        <meta name="keywords" content="master number, numerology calculator, master number meaning, intuition, manifestation, compassion" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourwebsite.com/calculator/master-number" />
        <meta property="og:title" content="Master Number Calculator | Calculate Your Master Number" />
        <meta property="og:description" content="Calculate your master number using numerology. Understand your potential for intuition, manifestation, and compassion." />
        <meta property="og:image" content="https://yourwebsite.com/images/master-number-calculator.png" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://yourwebsite.com/calculator/master-number" />
        <meta property="twitter:title" content="Master Number Calculator | Calculate Your Master Number" />
        <meta property="twitter:description" content="Calculate your master number using numerology. Understand your potential for intuition, manifestation, and compassion." />
        <meta property="twitter:image" content="https://yourwebsite.com/images/master-number-calculator.png" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🌟</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Master Number Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Calculate Your Master Number</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
              Name:
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter your name"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="birthDate">
              Birth Date:
            </label>
            <input
              type="date"
              id="birthDate"
              name="birthDate"
              value={birthDate}
              onChange={(e) => handleInputChange('birthDate', e.target.value)}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            />
          </div>
        </div>

        <button
          onClick={calculateMasterNumber}
          className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white mt-8"
        >
          Calculate Master Number
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Master Number Analysis</h3>
            
            <div className="space-y-12">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-medium mb-4">Your Details</h4>
                      <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">Name:</span>
                          <span className="font-medium text-blue-600">{results.name}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">Birth Date:</span>
                          <span className="font-medium text-blue-600">{results.birthDate}</span>
                        </div>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                        <h5 className="font-medium mb-2">Life Path Number:</h5>
                        <p className="text-blue-600 text-2xl text-center">{results.lifePathNumber}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                        <h5 className="font-medium mb-2">Master Number:</h5>
                        <p className="text-blue-600 text-2xl text-center">{results.masterNumber}</p>
                        <p className="text-gray-600 text-sm mt-2">{results.masterNumberMeaning}</p>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium mb-4">Master Factors</h4>
                      <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                        <h5 className="font-medium mb-2">Overall Master Score:</h5>
                        <div className="text-4xl font-bold text-blue-600 text-center">{results.overallMasterScore}%</div>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                        <h5 className="font-medium mb-2">Master Factors Breakdown:</h5>
                        <div className="space-y-4">
                          {Object.entries(results.masterFactors).map(([factor, score]) => (
                            <div key={factor} className="flex justify-between items-center">
                              <span className="text-gray-600">{factor.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</span>
                              <span className="text-blue-600 font-medium">{score}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Master Factors Visualization */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="text-xl font-semibold mb-6">Master Factors Visualization</h4>
                <div className="h-96 bg-gray-50 rounded-xl p-6">
                  <Radar
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Master Factors Analysis',
                          color: '#36A2EB',
                          font: {
                            size: 16
                          }
                        },
                        legend: {
                          display: true,
                          position: 'bottom',
                          labels: {
                            color: '#1F2937',
                            font: {
                              size: 12
                            }
                          }
                        }
                      },
                      scales: {
                        r: {
                          angleLines: {
                            color: '#E5E7EB'
                          },
                          grid: {
                            color: '#E5E7EB'
                          },
                          ticks: {
                            color: '#1F2937'
                          }
                        }
                      },
                      animation: {
                        duration: 1000,
                        easing: 'easeInOutQuart'
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Master Factors <span className="ml-2 text-2xl">📚</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Intuition</h3>
              <p className="text-gray-700">
                Measures your level of intuition and spiritual insight.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Manifestation</h3>
              <p className="text-gray-700">
                Evaluates your ability to manifest and build your dreams.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Compassion</h3>
              <p className="text-gray-700">
                Assesses your level of compassion and ability to teach and guide others.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to calculate your master number:
          </p>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Information</h3>
              <p className="text-gray-700">
                Enter your name and birth date.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Calculate</h3>
              <p className="text-gray-700">
                Click the "Calculate Master Number" button to see the results.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Understand Results</h3>
              <p className="text-gray-700">
                Review the master factors and radar chart to understand your master number potential.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Result Explanation <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Life Path Number</h3>
              <p className="text-gray-700">
                Your Life Path Number is calculated using your birth date. It represents your journey and the lessons you need to learn in this lifetime.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Master Number</h3>
              <p className="text-gray-700">
                Your Master Number is derived from both your name and birth date. This number signifies your potential for mastery and spiritual enlightenment.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Master Number Meaning</h3>
              <p className="text-gray-700">
                Each Master Number has a specific meaning. It provides insights into various aspects of your life, such as intuition, manifestation, and compassion.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 4: Master Factors Breakdown</h3>
              <p className="text-gray-700">
                The Master Factors Breakdown gives you a detailed analysis of your strengths and areas for improvement. Each factor is scored to help you understand your potential in different areas.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 5: Overall Master Score</h3>
              <p className="text-gray-700">
                Your Overall Master Score is an average of all the master factors. It gives you a general idea of your mastery potential.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2">What is a Master Number?</h3>
              <p className="text-gray-700">
                A Master Number is calculated using numerology to help understand your potential for mastery in various areas of life.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">How is the master number calculated?</h3>
              <p className="text-gray-700">
                The calculator analyzes your name and birth date to determine your life path number and master number.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">What do the master factors mean?</h3>
              <p className="text-gray-700">
                The master factors represent different areas of life where you have potential for mastery, including intuition, manifestation, and compassion.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Master number calculation</li>
                <li>Visual master factors radar chart</li>
                <li>Detailed master factors breakdown</li>
                <li>Life path number calculation</li>
                <li>Mastery potential analysis</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Understand your mastery potential</li>
                <li>Identify key strengths and areas for improvement</li>
                <li>Gain insights into your intuition, manifestation, and compassion</li>
                <li>Support personal and spiritual growth</li>
                <li>Enhance self-awareness and confidence in your abilities</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterNumberCalculator;